/**
 * @module StatusOverrideControl
 * @purpose Manual, audited status-override control for a collection request -- dimension picker (package|return) scoped to that dimension's vocabulary, plus mandatory reason (PRP §2 C7).
 * @depends useCollectionRequestMutations, ALL_PACKAGE_STATUSES/ALL_RETURN_STATUSES, collection-request-status.labels
 * @dependedOnBy ExpandedRequestDetail
 *
 * @rules
 * - State: local open/dimension/status/reason draft state; mutation loading/error via useCollectionRequestMutations
 * - Errors: ApiError message surfaced inline via the hook's `error`
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Wrench } from 'lucide-react'
import { useCollectionRequestMutations } from '../../../hooks'
import {
  ALL_PACKAGE_STATUSES,
  ALL_RETURN_STATUSES,
  type OverrideRequestStatusInput,
  type PackageStatus,
  type ReturnStatus,
} from '../../../types/collection-requests'
import {
  packageStatusLabel,
  returnStatusLabelAny,
} from '../../../components/collections/collection-request-status-badge/collection-request-status.labels'

type Dimension = OverrideRequestStatusInput['dimension']

interface StatusOverrideControlProps {
  caseId: string
  requestId: string
  packageStatus: PackageStatus
  returnStatus: ReturnStatus
  onOverridden: () => void
}

const FIELD_CLASS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'

function valueOptionsFor(t: TFunction<'cases'>, dimension: Dimension): { value: string; label: string }[] {
  if (dimension === 'package') {
    return ALL_PACKAGE_STATUSES.map((value) => ({ value, label: packageStatusLabel(t, value) }))
  }
  return ALL_RETURN_STATUSES.map((value) => ({ value, label: returnStatusLabelAny(t, value) }))
}

interface OverrideFormProps {
  dimension: Dimension
  status: string
  reason: string
  updating: boolean
  error: string | null
  onDimensionChange: (value: Dimension) => void
  onStatusChange: (value: string) => void
  onReasonChange: (value: string) => void
  onApply: () => void
  onCancel: () => void
}

function DimensionAndValueFields(props: OverrideFormProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { dimension, status, onDimensionChange, onStatusChange } = props
  return (
    <>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {t('collections.overrideDimensionLabel')}
        </span>
        <select
          value={dimension}
          onChange={(event) => {
            onDimensionChange(event.target.value as Dimension)
          }}
          className={FIELD_CLASS}
        >
          <option value="package">{t('collections.overrideDimensionPackage')}</option>
          <option value="return">{t('collections.overrideDimensionReturn')}</option>
        </select>
      </label>
      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {t('collections.overrideStatusNewLabel')}
        </span>
        <select
          value={status}
          onChange={(event) => {
            onStatusChange(event.target.value)
          }}
          className={FIELD_CLASS}
        >
          {valueOptionsFor(t, dimension).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </>
  )
}

function OverrideForm(props: OverrideFormProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { reason, updating, error, onReasonChange, onApply, onCancel } = props
  const canApply = reason.trim().length > 0 && !updating

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <div>
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {t('collections.overrideStatusTitle')}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t('collections.overrideStatusHint')}
        </p>
      </div>

      <DimensionAndValueFields {...props} />

      <label className="block space-y-1">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
          {t('collections.overrideStatusReasonLabel')}
        </span>
        <input
          type="text"
          value={reason}
          onChange={(event) => {
            onReasonChange(event.target.value)
          }}
          placeholder={t('collections.overrideStatusReasonPlaceholder')}
          className={FIELD_CLASS}
        />
      </label>

      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          {t('collections.overrideStatusCancel')}
        </button>
        <button
          onClick={onApply}
          disabled={!canApply}
          className="rounded-lg bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updating
            ? t('collections.overrideStatusApplying')
            : t('collections.overrideStatusApply')}
        </button>
      </div>
    </div>
  )
}

export function StatusOverrideControl({
  caseId,
  requestId,
  packageStatus,
  returnStatus,
  onOverridden,
}: StatusOverrideControlProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { overrideStatus, updating, error } = useCollectionRequestMutations()
  const [open, setOpen] = useState(false)
  const [dimension, setDimension] = useState<Dimension>('package')
  const [status, setStatus] = useState<string>(packageStatus)
  const [reason, setReason] = useState('')

  const handleDimensionChange = (value: Dimension): void => {
    setDimension(value)
    setStatus(value === 'package' ? packageStatus : returnStatus)
  }

  const handleApply = (): void => {
    void (async () => {
      const result = await overrideStatus(caseId, requestId, {
        dimension,
        status,
        reason: reason.trim(),
      })
      if (result) {
        setOpen(false)
        setReason('')
        onOverridden()
      }
    })()
  }

  if (!open) {
    return (
      <button
        onClick={() => {
          setDimension('package')
          setStatus(packageStatus)
          setReason('')
          setOpen(true)
        }}
        className="items-centre inline-flex gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
      >
        <Wrench className="h-3.5 w-3.5" />
        {t('collections.overrideStatus')}
      </button>
    )
  }

  return (
    <OverrideForm
      dimension={dimension}
      status={status}
      reason={reason}
      updating={updating}
      error={error}
      onDimensionChange={handleDimensionChange}
      onStatusChange={setStatus}
      onReasonChange={setReason}
      onApply={handleApply}
      onCancel={() => {
        setOpen(false)
      }}
    />
  )
}
